import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { toBackendUrl } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import StatCard from "@/components/StatCard";
import CampaignsList from "@/components/CampaignsList";
import LeadsTable from "@/components/LeadsTable";
import ActionsManager from "@/components/ActionsManager";
import AddCampaignDialog from "@/components/AddCampaignDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/contexts/DataContext";
import {
  Users,
  CheckCircle,
  QrCode,
  TrendingUp,
  BarChart3,
  Info,
  Eye,
} from "lucide-react";
import TimeseriesChart from "@/components/TimeseriesChart";
import PieChart from "@/components/PieChart";
import ActionMultiSelect from "@/components/ActionMultiSelect";
import { api } from "@/api";
import LogoCropModal from "@/components/LogoCropModal";
import styles from "@/styles/pages/MerchantDashboard.module.css";

// Simplified initial merchant dashboard (Phase C not complete yet)
const MerchantDashboard = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { campaigns, leads, codes, actions } = useData();
  const { token } = useAuth();
  const location = useLocation();
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState(null);
  const [actionPerf, setActionPerf] = useState(null);
  const [rangeLeads, setRangeLeads] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);
  // New: custom range + action filter
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [endDate, setEndDate] = useState("");
  const [actionQuery, setActionQuery] = useState("");
  const [actionOptions, setActionOptions] = useState([]);
  const [selectedActions, setSelectedActions] = useState([]); // [{id,title}]
  const [searchLoading, setSearchLoading] = useState(false);
  const [chartMode, setChartMode] = useState(() => localStorage.getItem('pref_chart_mode') || 'line'); // 'line' | 'pie'
  useEffect(()=>{ localStorage.setItem('pref_chart_mode', chartMode); }, [chartMode]);
  const activeDropdownOptions = React.useMemo(()=>{
    try {
      const list = Array.isArray(actions) ? actions.filter(a=>a?.active).slice() : [];
      // Prefer most recently created first
      list.sort((a,b)=> new Date(b.createdAt||0) - new Date(a.createdAt||0));
      return list.slice(0,10).map(a=>({ id: a.id, title: a.title }));
    } catch { return []; }
  }, [actions]);
  const fetchDay = async (date) => {
    if (!token) return;
    setDayLoading(true);
    try {
      const d = await api.analyticsDay(token, date);
      setDayDetail(d);
    } catch (e) {
      /*ignore*/
    } finally {
      setDayLoading(false);
    }
  };
  const [days, setDays] = useState(() =>
    parseInt(localStorage.getItem("pref_days_merchant") || "30")
  );
  useEffect(() => {
    localStorage.setItem("pref_days_merchant", String(days));
  }, [days]);
  const loadSeries = React.useCallback(async () => {
    if (!token) return;
    try {
      const o = await api.analyticsOverview(token);
      setOverview(o);
      const useActions = selectedActions.length > 0;
      const useCustomRange = !!(startDate && endDate);
      const computeRangeFromDays = () => {

        const today = new Date();
        const utcY = today.getUTCFullYear();
        const utcM = today.getUTCMonth();
        const utcD = today.getUTCDate();
        const end = new Date(Date.UTC(utcY, utcM, utcD));
        const start = new Date(end.getTime() - (days - 1) * 86400000);
        const fmt = (d) => d.toISOString().substring(0,10);
        return { start: fmt(start), end: fmt(end) };
      };
      if (useCustomRange || useActions) {
        const range = useCustomRange ? { start: startDate, end: endDate } : computeRangeFromDays();
        const granularity = (range.start === range.end) ? 'hour' : 'day';
        const [tsRaw, lr] = await Promise.all([
          api.analyticsRange(token, { start: range.start, end: range.end, actionIds: selectedActions.map(a=>a.id), granularity, includeMeta: true }),
          api.analyticsLeadsRange(token, { start: range.start, end: range.end, actionIds: selectedActions.map(a=>a.id) }),
        ]);
        let ts = tsRaw;
        const oneDay = range.start === range.end;
        // Fallback: if we expected hourly but backend returned daily or empty labels, derive hourly from /analytics/day
        if (oneDay && (!tsRaw || tsRaw.granularity !== 'hour' || (Array.isArray(tsRaw.labels) && tsRaw.labels.length <= 1))) {
          try {
            const day = await api.analyticsDay(token, range.start);
            const labels = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2,'0')}:00`);
            const scansH = new Array(24).fill(0);
            const redeemsH = new Array(24).fill(0);
            if (day && Array.isArray(day.scans)) {
              for (const s of day.scans) {
                const h = new Date(s.at).getUTCHours();
                scansH[h] += 1;
              }
            }
            if (day && Array.isArray(day.redeems)) {
              for (const r of day.redeems) {
                const h = new Date(r.at).getUTCHours();
                redeemsH[h] += 1;
              }
            }
            ts = { labels, scans: scansH, redeems: redeemsH, granularity: 'hour' };
          } catch(_) { /* keep tsRaw */ }
        }
        // Normalize hour labels to HH:00
        if (ts && ts.granularity === 'hour' && Array.isArray(ts.labels)) {
          ts = { ...ts, labels: ts.labels.map((h)=>/^\d{2}(:00)?$/.test(h) ? (h.includes(':')?h:`${h}:00`) : String(h)) };
        }
        setSeries(ts);
        setRangeLeads(Number(lr?.total || 0));
      } else {
        const ts = await api.analyticsTimeseries(token, days);
        setSeries(ts);
        // Also compute range from days for leads to keep cards aligned with chart presets
        const range = computeRangeFromDays();
        try {
          const lr = await api.analyticsLeadsRange(token, { start: range.start, end: range.end, actionIds: [] });
          setRangeLeads(Number(lr?.total || 0));
        } catch(_) { setRangeLeads(null); }
      }
      // Load per-action performance table (same date range as chart)
      try {
        const range = (startDate && endDate) ? { start: startDate, end: endDate } : (() => {
          const today = new Date();
          const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
          const start = new Date(end.getTime() - (days - 1) * 86400000);
          const fmt = (d) => d.toISOString().substring(0, 10);
          return { start: fmt(start), end: fmt(end) };
        })();
        const perf = await api.analyticsActionsPerformance(token, range);
        setActionPerf(perf?.actions || []);
      } catch (_) { setActionPerf([]); }
    } catch (_) { /*ignore*/ }
  }, [token, days, startDate, endDate, selectedActions]);

  useEffect(() => { loadSeries(); }, [loadSeries]);

  // Search actions for filter
  useEffect(() => {
    let alive = true;
    if (!token) return;
    const q = actionQuery.trim();
    if (!q) { setActionOptions([]); return; }
    setSearchLoading(true);
    api.searchActions(token, { q, limit: 20 })
      .then((list) => { if (alive) setActionOptions(list.map(a => ({ id: a.id, title: a.title })) ); })
      .catch(() => { if (alive) setActionOptions([]); })
      .finally(() => { if (alive) setSearchLoading(false); });
    return () => { alive = false; };
  }, [token, actionQuery]);

  // Stats from filtered range (aligned with chart)
  const filteredScans = series ? (Array.isArray(series.scans) ? series.scans.reduce((a,b)=>a+b,0) : 0) : (overview?.scans ?? 0);
  const filteredRedeems = series ? (Array.isArray(series.redeems) ? series.redeems.reduce((a,b)=>a+b,0) : 0) : (overview?.redeems ?? 0);
  const filteredPageViews = series ? (Array.isArray(series.pageViews) ? series.pageViews.reduce((a,b)=>a+b,0) : 0) : (overview?.pageViews ?? 0);
  const totalScans = filteredScans;
  const totalRedeems = filteredRedeems;
  const redeemedCodes = totalRedeems;
  const totalBudget = campaigns.reduce(
    (sum, c) => sum + (parseFloat(c.budget) || 0),
    0
  );
  const leadsValueTotal = totalScans;
  const cpl = leadsValueTotal ? (totalBudget / leadsValueTotal).toFixed(2) : "0.00";
  const cpredeem = totalRedeems ? (totalBudget / totalRedeems).toFixed(2) : "0.00";
  const conversionRate = leadsValueTotal > 0 ? ((totalRedeems / leadsValueTotal) * 100).toFixed(1) : "0";

  const isEmployee = user?.role === "EMPLOYEE";
  const [showScans, setShowScans] = useState(true);
  const [showRedeems, setShowRedeems] = useState(true);
  const [showPageViews, setShowPageViews] = useState(false);
  const [showMetaSpend, setShowMetaSpend] = useState(false);
  const [showMetaReach, setShowMetaReach] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("campaigns");

  // React to URL hash like #integrations to open the correct tab
  useEffect(() => {
    const hash = location.hash ? location.hash.replace('#', '') : '';
    if (!hash) return;
    // Only handle simple tab names, ignore things like #actions-for=...
    if (["campaigns", "actions", "leads", "settings", "integrations"].includes(hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  return (
    isEmployee ? (
      <Navigate to="/redeemer" replace />
    ) : (
    <div className={styles.container}>
      <main className={styles.main}>
        <h1 className={styles.header}>{t('nav.dashboard')}</h1>
        {
          <div className={styles.statsGrid}>
            <StatCard icon={Users} title={t('merchant.stats.leads')} value={leadsValueTotal} />
            <StatCard
              icon={CheckCircle}
              title={t('merchant.stats.redeemed')}
              value={totalRedeems}
            />
            <StatCard
              icon={Eye}
              title={t('merchant.stats.pageViews', 'Seitenaufrufe')}
              value={filteredPageViews}
            />
            <StatCard
              icon={QrCode}
              title={t('merchant.stats.budget')}
              value={totalBudget.toFixed(2) + "€"}
            />
            <StatCard
              icon={TrendingUp}
              title={t('merchant.stats.conversion')}
              value={`${conversionRate}%`}
            />
          </div>
        }
        {
          <div className={styles.analyticsContainer}>
            <div className={styles.analyticsHeader}>
              <BarChart3 className="h-4 w-4" />
              <span className={styles.tabTriggerTextLong}>{t('merchant.analytics.title')}</span>
              <span className={styles.tabTriggerTextShort}>{t('merchant.analytics.title')}</span>
            </div>
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1 px-2 py-1 text-xs border rounded md:hidden"
              onClick={() => setFiltersOpen((v) => !v)}
            >
              {filtersOpen ? t('common.hideFilters', 'Filter ausblenden') : t('common.showFilters', 'Filter anzeigen')}
            </button>
            <div className="mt-3 flex flex-col md:flex-row gap-4 items-stretch">
              <div className="flex-1">
                <div className="bg-card border rounded-lg p-3 shadow-sm h-full flex flex-col">
                  {series ? (
                    chartMode === 'line' ? (
                      <>
                        <TimeseriesChart
                          labels={series.labels}
                          scans={series.scans}
                          redeems={series.redeems}
                          pageViews={series.pageViews || []}
                          showScans={showScans}
                          showRedeems={showRedeems}
                          showPageViews={showPageViews}
                          metaSpend={series.meta?.spend || []}
                          metaReach={series.meta?.reach || []}
                          showMetaSpend={showMetaSpend}
                          showMetaReach={showMetaReach}
                          onSelect={series.granularity === 'hour' && startDate ? (()=>fetchDay(startDate)) : fetchDay}
                        />
                        <div className="mt-3 flex items-center gap-4 text-[11px] text-muted-foreground flex-wrap">
                          <div className="flex items-center gap-1">
                            <span className="inline-block w-2 h-2 rounded-full bg-blue-600" />
                            {t('merchant.analytics.labels.scans')}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="inline-block w-2 h-2 rounded-full bg-green-600" />
                            {t('merchant.analytics.labels.redeems')}
                          </div>
                          {showPageViews && (
                            <div className="flex items-center gap-1">
                              <span className="inline-block w-2 h-2 rounded-full" style={{background:'#6366f1'}} />
                              {t('merchant.analytics.labels.pageViews', 'Seitenaufrufe')}
                            </div>
                          )}
                          {series.meta && (
                            <>
                              <div className="flex items-center gap-1">
                                <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
                                {t('merchant.analytics.labels.metaBudget', 'Meta Budget')}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="inline-block w-2 h-2 rounded-full bg-purple-500" />
                                {t('merchant.analytics.labels.metaReach', 'Meta Reichweite')}
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-4">
                        {showScans && (
                          <div>
                            <div className="text-xs text-muted-foreground mb-1">{t('merchant.analytics.labels.scans')}</div>
                            <PieChart labels={series.labels} values={series.scans} />
                          </div>
                        )}
                        {showRedeems && (
                          <div>
                            <div className="text-xs text-muted-foreground mb-1">{t('merchant.analytics.labels.redeems')}</div>
                            <PieChart labels={series.labels} values={series.redeems} />
                          </div>
                        )}
                        {!showScans && !showRedeems && (
                          <div className="text-xs text-muted-foreground col-span-full">
                            {t('merchant.analytics.noSeries')}
                          </div>
                        )}
                      </div>
                    )
                  ) : (
                    <div className={styles.analyticsLoading}>{t('common.loading')}</div>
                  )}
                      {(series || overview) && (
                        <div className={`${styles.analyticsOverview} mt-3`}>
                    {series ? (
                      <>
                        <span>
                          {t('merchant.analytics.overview.periodScans', { count: (Array.isArray(series.scans) ? series.scans.reduce((a, b) => a + b, 0) : 0) })}
                        </span>
                        <span>
                          {t('merchant.analytics.overview.periodRedeems', { count: (Array.isArray(series.redeems) ? series.redeems.reduce((a, b) => a + b, 0) : 0) })}
                        </span>
                        <span>
                          {t('merchant.analytics.overview.periodPageViews', { count: (Array.isArray(series.pageViews) ? series.pageViews.reduce((a, b) => a + b, 0) : 0), defaultValue: '{{count}} Seitenaufrufe' })}
                        </span>
                        {overview && (
                          <span className="text-muted-foreground">
                            {t('merchant.analytics.overview.totalScans', { count: overview.scans })} · {t('merchant.analytics.overview.totalRedeems', { count: overview.redeems })} · {t('merchant.analytics.overview.totalPageViews', { count: overview.pageViews ?? 0, defaultValue: '{{count}} Aufrufe gesamt' })}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <span>{t('merchant.analytics.overview.totalScans', { count: overview.scans })}</span>
                        <span>{t('merchant.analytics.overview.totalRedeems', { count: overview.redeems })}</span>
                        <span>{t('merchant.analytics.overview.totalPageViews', { count: overview.pageViews ?? 0, defaultValue: '{{count}} Aufrufe gesamt' })}</span>
                      </>
                    )}
                  </div>
                )}
                </div>
              </div>
              <aside className={`w-full md:w-64 lg:w-72 flex-shrink-0 ${filtersOpen ? '' : 'hidden md:block'}`}>
                <div className="bg-card border rounded-lg shadow-sm p-3 space-y-3 text-xs">
                  <div className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wide">
                    {t('merchant.analytics.filters')}
                  </div>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] text-muted-foreground">{t('merchant.analytics.chart')}</span>
                    <select
                      value={chartMode}
                      onChange={(e)=>setChartMode(e.target.value)}
                      className="border rounded px-2 py-1 text-xs"
                    >
                      <option value="line">{t('merchant.analytics.mode.line')}</option>
                      <option value="pie">{t('merchant.analytics.mode.pie')}</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] text-muted-foreground">{t('merchant.analytics.period')}</span>
                    <select
                      value={days}
                      onChange={(e) => { setDays(parseInt(e.target.value)); setStartDate(""); setEndDate(""); }}
                      className="border rounded px-2 py-1 text-xs"
                    >
                      <option value={7}>7</option>
                      <option value={30}>30</option>
                      <option value={90}>90</option>
                    </select>
                  </label>
                  <div className="flex flex-col gap-1">
                    <span className="text-[11px] text-muted-foreground">{t('merchant.analytics.from')}</span>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e)=>{ setStartDate(e.target.value); }}
                      className="border rounded px-2 py-1 text-xs"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[11px] text-muted-foreground">{t('merchant.analytics.to')}</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e)=>{ setEndDate(e.target.value); }}
                      className="border rounded px-2 py-1 text-xs"
                    />
                  </div>
                  <div className="flex flex-col gap-1" style={{ minWidth: 0 }}>
                    <span className="text-[11px] text-muted-foreground">{t('merchant.analytics.action')}</span>
                    <ActionMultiSelect
                      token={token}
                      selected={selectedActions}
                      onChange={setSelectedActions}
                      initialOptions={activeDropdownOptions}
                    />
                  </div>
                  {(startDate && endDate) && (
                    <button
                      className="w-full border rounded px-2 py-1 text-xs bg-blue-600 text-white"
                      onClick={()=>loadSeries()}
                    >
                      {t('merchant.analytics.apply')}
                    </button>
                  )}
                  {(startDate || endDate || selectedActions.length>0) && (
                    <button
                      className="w-full border rounded px-2 py-1 text-xs"
                      onClick={()=>{ setStartDate(""); setEndDate(""); setSelectedActions([]); setActionQuery(""); loadSeries(); }}
                    >
                      {t('merchant.analytics.reset')}
                    </button>
                  )}
                  <div className="border-t pt-3 mt-1 space-y-2">
                    <div className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wide">
                      {t('merchant.analytics.series')}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={()=>setShowScans((v)=>!v)}
                        className={`flex-1 px-2 py-1 rounded-full text-[11px] border ${showScans ? 'bg-blue-50 dark:bg-blue-950 border-blue-500 text-blue-700 dark:text-blue-300' : 'bg-background text-muted-foreground'}`}
                      >
                        {t('merchant.analytics.labels.scans')}
                      </button>
                      <button
                        type="button"
                        onClick={()=>setShowRedeems((v)=>!v)}
                        className={`flex-1 px-2 py-1 rounded-full text-[11px] border ${showRedeems ? 'bg-green-50 dark:bg-green-950 border-green-500 text-green-700 dark:text-green-300' : 'bg-background text-muted-foreground'}`}
                      >
                        {t('merchant.analytics.labels.redeems')}
                      </button>
                      <button
                        type="button"
                        onClick={()=>setShowPageViews((v)=>!v)}
                        className={`flex-1 px-2 py-1 rounded-full text-[11px] border ${showPageViews ? 'border-indigo-500 text-indigo-700 dark:text-indigo-300' : 'bg-background text-muted-foreground'}`}
                        style={showPageViews ? {background:'rgba(99,102,241,0.08)'} : {}}
                      >
                        {t('merchant.analytics.labels.pageViews', 'Aufrufe')}
                      </button>
                      {series?.meta && (
                        <>
                          <button
                            type="button"
                            onClick={()=>setShowMetaSpend((v)=>!v)}
                            className={`flex-1 px-2 py-1 rounded-full text-[11px] border ${showMetaSpend ? 'bg-orange-50 dark:bg-orange-950 border-orange-500 text-orange-700 dark:text-orange-300' : 'bg-background text-muted-foreground'}`}
                          >
                            {t('merchant.analytics.labels.metaBudget', 'Meta Budget')}
                          </button>
                          <button
                            type="button"
                            onClick={()=>setShowMetaReach((v)=>!v)}
                            className={`flex-1 px-2 py-1 rounded-full text-[11px] border ${showMetaReach ? 'bg-purple-50 dark:bg-purple-950 border-purple-500 text-purple-700 dark:text-purple-300' : 'bg-background text-muted-foreground'}`}
                          >
                            {t('merchant.analytics.labels.metaReach', 'Meta Reichweite')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        }

        {/* Per-action KPI table */}
        {actionPerf && <ActionPerfTable data={actionPerf} t={t} />}

        {dayDetail && (
          <div className={styles.modal}>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <h4 className={styles.modalTitle}>
                  {t('merchant.modal.title', { date: dayDetail.date })}
                </h4>
                <button
                  onClick={() => setDayDetail(null)}
                  className={styles.modalCloseButton}
                >
                  {t('merchant.modal.close')}
                </button>
              </div>
              {dayLoading && (
                <div className={styles.modalLoading}>{t('merchant.modal.updating')}</div>
              )}
              <div className={styles.modalList}>
                {dayDetail.scans.map((s) => (
                  <div key={"s" + s.id} className={styles.modalListItem}>
                    <span className={`${styles.modalItemType} ${styles.modalItemTypeScan}`}>SCAN</span>
                    <span className={styles.modalItemTitle}>
                      {s.actionTitle}
                    </span>
                    <span className={styles.modalItemTime}>
                      {new Date(s.at).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
                {dayDetail.redeems.map((r) => (
                  <div key={"r" + r.id} className={styles.modalListItem}>
                    <span className={`${styles.modalItemType} ${styles.modalItemTypeRedeem}`}>REDEEM</span>
                    <span className={styles.modalItemTitle}>
                      {r.actionTitle}{r.byEmail ? ` – ${r.byEmail}` : ''}
                    </span>
                    <span className={styles.modalItemTime}>
                      {new Date(r.at).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
                {dayDetail.scans.length === 0 &&
                  dayDetail.redeems.length === 0 && (
                    <div className={styles.modalEmpty}>
                      {t('merchant.modal.empty')}
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
        {
          <div className={styles.metricsGrid}>
            <div className={styles.metricCard}>
              <div className={styles.metricContent}>
                <span className={styles.metricLabel}>{t('merchant.metrics.cpl')}</span>
                <strong className={styles.metricValue}>{cpl}€</strong>
              </div>
            </div>
            <div className={styles.metricCard}>
              <div className={styles.metricContent}>
                <span className={styles.metricLabel}>{t('merchant.metrics.cpredeem')}</span>
                <strong className={styles.metricValue}>{cpredeem}€</strong>
              </div>
            </div>
          </div>
        }
  <Tabs value={activeTab} onValueChange={setActiveTab} className={styles.tabsContainer}>
          <div className={styles.tabsScrollContainer}>
            <TabsList className={styles.tabsList}>
              <TabsTrigger value="campaigns" className={styles.tabTrigger}>
                <span className={styles.tabTriggerTextLong}>{t('merchant.tabs.campaigns')}</span>
                <span className={styles.tabTriggerTextShort}>{t('merchant.tabs.campaignsShort')}</span>
              </TabsTrigger>
              <TabsTrigger value="actions" className={styles.tabTrigger}>
                <span className={styles.tabTriggerTextLong}>{t('merchant.tabs.actions')}</span>
                <span className={styles.tabTriggerTextShort}>{t('merchant.tabs.actionsShort')}</span>
              </TabsTrigger>
              <TabsTrigger value="leads" className={styles.tabTrigger}>{t('merchant.tabs.leads')}</TabsTrigger>
              {user?.role === 'ORG_OWNER' && (
                <TabsTrigger value="settings" className={styles.tabTrigger}>
                  <span className={styles.tabTriggerTextLong}>{t('merchant.tabs.settings')}</span>
                  <span className={styles.tabTriggerTextShort}>{t('merchant.tabs.settingsShort')}</span>
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <TabsContent value="campaigns">
            <section>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">{t('merchant.tabs.campaigns')}</h2>
                <AddCampaignDialog />
              </div>
              <CampaignsList onOpenActionsForCampaign={(id)=>{
                // Switch to Actions tab and set a query param/hash to filter
                try {
                  const url = new URL(window.location.href);
                  url.hash = '#actions-for=' + id;
                  history.replaceState(null, '', url.toString());
                  document.querySelector('[role="tab"][data-state]')?.blur();
                  // Click Actions tab trigger
                  const btn = document.querySelector('[data-radix-collection-item][data-state][value="actions"], [role="tab"][data-value="actions"]');
                  if (btn && typeof btn.click === 'function') {
                    btn.click();
                  }
                } catch(_) {}
              }} />
            </section>
          </TabsContent>

          <TabsContent value="actions">
            <section>
              <h2 className="text-xl font-semibold mb-4">{t('merchant.tabs.actions')}</h2>
              <ActionsManager />
            </section>
          </TabsContent>

          <TabsContent value="leads">
            <section>
              <h2 className="text-xl font-semibold mb-4">{t('merchant.tabs.leads')}</h2>
              <LeadsTable />
            </section>
          </TabsContent>

          {user?.role === 'ORG_OWNER' && (
            <TabsContent value="settings">
              <OrgSettings />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
    )
  );
};

export default MerchantDashboard;

const PER_PAGE = 5;

function ActionPerfTable({ data, t }) {
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(0);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.filter(a => a.title.toLowerCase().includes(q)) : data;
  }, [data, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const visible = filtered.slice(safePage * PER_PAGE, safePage * PER_PAGE + PER_PAGE);

  React.useEffect(() => { setPage(0); }, [search]);

  if (!data.length) return null;

  return (
    <div className="mt-4 bg-card border rounded-lg shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b flex items-center gap-3">
        <Eye className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex-1">
          {t('merchant.analytics.perAction', 'Performance je Aktion')}
        </span>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('merchant.analytics.searchAction', 'Aktion suchen…')}
          className="border rounded px-2 py-1 text-xs w-44 bg-background"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">{t('merchant.analytics.actionTitle', 'Aktion')}</th>
              <th className="text-right px-3 py-2 font-medium text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full" style={{background:'#6366f1'}} />
                  {t('merchant.analytics.labels.pageViews', 'Aufrufe')}
                </span>
              </th>
              <th className="text-right px-3 py-2 font-medium text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-blue-600" />
                  {t('merchant.analytics.labels.scans', 'Scans')}
                </span>
              </th>
              <th className="text-right px-3 py-2 font-medium text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-600" />
                  {t('merchant.analytics.labels.redeems', 'Einlösungen')}
                </span>
              </th>
              <th className="text-right px-4 py-2 font-medium text-muted-foreground">Conv.</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">{t('common.noResults', 'Keine Ergebnisse')}</td></tr>
            ) : visible.map(a => {
              const conv = a.scans > 0 ? ((a.redeems / a.scans) * 100).toFixed(0) : '—';
              return (
                <tr key={a.id} className={`border-b last:border-0 hover:bg-muted/30 transition-colors ${!a.active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-2 max-w-[200px] font-medium">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {!a.active && <span className="shrink-0 text-[10px] bg-muted text-muted-foreground px-1 rounded">inaktiv</span>}
                      <span className="truncate">{a.title}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums" style={{color:'#6366f1'}}>{a.pageViews > 0 ? a.pageViews : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-blue-600">{a.scans > 0 ? a.scans : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-green-600">{a.redeems > 0 ? a.redeems : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{conv}{conv !== '—' ? '%' : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="px-4 py-2 border-t flex items-center justify-between text-xs text-muted-foreground">
          <span>{filtered.length} {t('merchant.analytics.actionsTotal', 'Aktionen')} · Seite {safePage + 1} / {totalPages}</span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="px-2 py-1 border rounded disabled:opacity-40 hover:bg-muted transition-colors"
            >‹</button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
              className="px-2 py-1 border rounded disabled:opacity-40 hover:bg-muted transition-colors"
            >›</button>
          </div>
        </div>
      )}
    </div>
  );
}

const orgStyles = {
  container: "bg-card p-3 sm:p-4 rounded shadow max-w-xl mx-2 sm:mx-0",
  loading: "bg-card p-3 sm:p-4 rounded shadow text-xs text-muted-foreground mx-2 sm:mx-0",
  title: "text-sm font-semibold mb-3",
  form: "space-y-3 text-sm",
  label: "text-[10px] text-muted-foreground mb-1",
  input: "w-full border rounded px-3 py-2 text-sm bg-background text-foreground",
  inputReadonly: "w-full border rounded px-3 py-2 text-sm bg-muted text-muted-foreground",
  logoContainer: "mt-2 flex justify-center",
  logoImage: "h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover ring-1 ring-border shadow",
  fileInputContainer: "mt-3 flex items-center gap-2 justify-center",
  fileInput:
    "text-xs w-full file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100",
  addressGrid: "grid grid-cols-1 sm:grid-cols-2 gap-3",
  submitButton: "w-full sm:w-auto min-h-[44px] sm:min-h-auto",
  note: "text-[11px] text-muted-foreground mt-3",
};

function OrgSettings(){
  const { t } = useTranslation();
  const { token, setCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [allowRemoteAccess, setAllowRemoteAccess] = useState(false);
  const [companyAgencyId, setCompanyAgencyId] = useState(null);
  const [agencyOptions, setAgencyOptions] = useState([]);
  // Add cropper state
  const [cropFile, setCropFile] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  // Cache-busting for logo preview
  const [logoBust, setLogoBust] = useState(0);
  React.useEffect(()=>{
    let mounted = true;
    (async()=>{
      try{
        const res = await api.myCompany(token);
        if(mounted){
          setName(res.name || "");
          setEmail(res.email || "");
          setLogoUrl(res.logoUrl || "");
          setAddressLine1(res.addressLine1 || "");
          setAddressLine2(res.addressLine2 || "");
          setPostalCode(res.postalCode || "");
          setCity(res.city || "");
          setCountry(res.country || "");
          setAllowRemoteAccess(res.allowRemoteAccess || false);
          setCompanyAgencyId(res.agencyId || null);
        }
      }catch(e){ /* ignore */ }
      finally{ if(mounted) setLoading(false); }
    })();
    return ()=>{ mounted=false; };
  },[token]);
  React.useEffect(() => {
    if (!token) return;
    let mounted = true;
    (async () => {
      try {
        const res = await api.companyAgencyOptions(token);
        if (!mounted) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        setAgencyOptions(items);
      } catch (_) {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);
  const save = async (e)=>{
    e.preventDefault();
    if(!name.trim()) { toast({ title: t('org.errors.enterName'), variant: 'destructive' }); return; }
    try{
      setSaving(true);
      await api.updateMyCompany(token, {
        name: name.trim(),
        logoUrl: logoUrl || null,
        addressLine1: addressLine1 || null,
        addressLine2: addressLine2 || null,
        postalCode: postalCode || null,
        city: city || null,
        country: country || null,
        allowRemoteAccess: allowRemoteAccess,
      });
      setCompany((c)=> ({ ...(c||{}), name: name.trim(), logoUrl }));
      toast({ title: t('org.saved') });
    }catch(e){ toast({ title: t('org.error', { defaultValue: 'Fehler' }), description: e.message, variant: 'destructive' }); }
    finally{ setSaving(false); }
  };
  if(loading) return <div className={orgStyles.loading}>{t('common.loading')}</div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Crop modal */}
      <LogoCropModal
        file={cropFile}
        open={cropOpen}
        onClose={() => { setCropOpen(false); setCropFile(null); }}
        onCropped={async (blob) => {
          setCropOpen(false);
          setCropFile(null);
          try {
            const file = new File([blob], "logo.png", { type: "image/png" });
            const res = await api.uploadCompanyLogo(token, file);
            const url = res?.url || '';
            setLogoUrl(url);
            setLogoBust(Date.now());
            setCompany((c)=> ({ ...(c||{}), logoUrl: url }));
            toast({ title: t('org.logoUpdated') });
          } catch (err) {
            toast({ title: t('org.uploadFailed'), description: err.message, variant: 'destructive' });
          }
        }}
      />
      {/* Organization settings card */}
      <div className={orgStyles.container}>
        <h3 className={orgStyles.title}>{t('org.title')}</h3>
        <form onSubmit={save} className={orgStyles.form}>
        <div>
          <div className={orgStyles.label}>{t('org.name')}</div>
          <input className={orgStyles.input} value={name} onChange={(e)=>setName(e.target.value)} />
        </div>
        <div>
          <div className={orgStyles.label}>{t('org.logo')}</div>
          {!!logoUrl && (
            <div className={orgStyles.logoContainer}>
              <img
                src={`${toBackendUrl(logoUrl)}${logoBust ? `?v=${logoBust}` : ''}`}
                alt={t('org.logo')}
                className={orgStyles.logoImage}
              />
            </div>
          )}
          <div className={orgStyles.fileInputContainer}>
            <input 
              type="file" 
              accept="image/*" 
              onChange={async (e)=>{
                const f = e.target.files?.[0];
                if(!f) return;
                // open cropper instead of direct upload
                setCropFile(f);
                setCropOpen(true);
              }} 
              className={orgStyles.fileInput} 
            />
          </div>
        </div>
        <div>
          <div className={orgStyles.label}>{t('org.address1')}</div>
          <input className={orgStyles.input} value={addressLine1} onChange={(e)=>setAddressLine1(e.target.value)} />
        </div>
        <div>
          <div className={orgStyles.label}>{t('org.address2')}</div>
          <input className={orgStyles.input} value={addressLine2} onChange={(e)=>setAddressLine2(e.target.value)} />
        </div>
        <div className={orgStyles.addressGrid}>
          <div>
            <div className={orgStyles.label}>{t('org.postalCode')}</div>
            <input className={orgStyles.input} value={postalCode} onChange={(e)=>setPostalCode(e.target.value)} />
          </div>
          <div>
            <div className={orgStyles.label}>{t('org.city')}</div>
            <input className={orgStyles.input} value={city} onChange={(e)=>setCity(e.target.value)} />
          </div>
        </div>
        <div>
          <div className={orgStyles.label}>{t('org.country')}</div>
          <input className={orgStyles.input} value={country} onChange={(e)=>setCountry(e.target.value)} />
        </div>
        <div>
          <div className={orgStyles.label}>{t('org.emailInfo')}</div>
          <input className={orgStyles.inputReadonly} value={email} readOnly />
        </div>
        <div>
          <div className={orgStyles.label}>{t('org.agency.label')}</div>
          <select
            className={orgStyles.input}
            value={companyAgencyId || ""}
            disabled
          >
            {companyAgencyId ? null : <option value="">{t('org.agency.none')}</option>}
            {agencyOptions.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <p className={orgStyles.note}>{t('org.agency.help')}</p>
        </div>
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded border border-blue-200">
          <input
            type="checkbox"
            id="allowRemoteAccess"
            checked={allowRemoteAccess}
            onChange={(e) => setAllowRemoteAccess(e.target.checked)}
            className="h-4 w-4"
          />
          <label htmlFor="allowRemoteAccess" className="text-sm text-blue-900 cursor-pointer">
            <span className="font-medium">{t('org.remoteAccess.label')}</span>
            <span className="block text-xs text-blue-700 mt-0.5">
              {t('org.remoteAccess.desc')}
            </span>
          </label>
        </div>
        <p className={orgStyles.note}>{t('org.nameNote')}</p>
        <Button type="submit" size="sm" disabled={saving} className={orgStyles.submitButton}>
          {saving? t('org.saving') : t('org.save')}
        </Button>
        </form>
      </div>
    </div>
  );
}
